import { ConnectProto } from "@racoon/api";
import { Namespace, type TFn, type TranslationKey } from "@racoon/i18n";
import { getEnumName } from "@racoon/lib";
import { fullName } from "~/shared/helpers";
import { jobIncidentRank } from "~/shared/lib/job-incident";

/**
 * Filters for the registry's columns the server cannot filter by. While one is set the
 * page loads the whole match and filters it here (`hasColumnFilter`), as the review-photo
 * mode does, so a filter never narrows only the rows loaded so far. Each is keyed in the
 * URL by its param.
 */
export const COLUMN_FILTERS = [
  "author",
  "ai",
  "incident",
  "containerType",
  "priority",
  "geozone",
  "route",
  "plate",
  "comment",
] as const;

export type ColumnFilter = (typeof COLUMN_FILTERS)[number];

/** The filters with just two values, yes and no: the panel shows each as a checkbox. */
export const YES_NO_FILTERS: ReadonlySet<ColumnFilter> = new Set(["ai", "comment"]);
export type ColumnFilterValues = Record<ColumnFilter, string>;
type IncidentsOf = (job: ConnectProto.Job) => readonly ConnectProto.DriverIncident[] | undefined;
type Option = { id: string; name: string };

/** The value a job has for no value: no author, no route, no comment, … */
const NONE = "none";

export const readColumnFilters = (params: URLSearchParams): ColumnFilterValues =>
  Object.fromEntries(
    COLUMN_FILTERS.map((key) => [key, params.get(key) ?? ""]),
  ) as ColumnFilterValues;

export const hasColumnFilter = (values: ColumnFilterValues): boolean =>
  COLUMN_FILTERS.some((key) => values[key] !== "");

/**
 * A job's value for a column filter, as an option id, and the option's name. Undefined
 * while it cannot be told yet (the job's incidents have not arrived).
 */
const valueOf = (
  key: ColumnFilter,
  job: ConnectProto.Job,
  t: TFn,
  incidentsOf: IncidentsOf,
): Option | undefined => {
  const none = { id: NONE, name: t(Namespace.Settings, "organizationLabels.undefined") };
  switch (key) {
    case "author": {
      const name = fullName(job.author);
      return job.authorId && name ? { id: String(job.authorId), name } : none;
    }
    case "ai":
      return job.visibleToAi
        ? { id: "yes", name: t(Namespace.Table, "yes") }
        : { id: "no", name: t(Namespace.Table, "no") };
    case "incident": {
      const incidents = incidentsOf(job);
      if (!incidents) return undefined;
      const rank = jobIncidentRank(job, incidents);
      if (rank === undefined) return none;
      if (rank === ConnectProto.DriverIncidentType.EXPIRED) {
        return { id: String(rank), name: t(Namespace.Orders, "jobMap.overdue") };
      }
      const type = getEnumName(ConnectProto.DriverIncidentTypeSchema, rank);
      return {
        id: String(rank),
        name: type ? t(Namespace.Enum, type as TranslationKey) : none.name,
      };
    }
    case "containerType": {
      const type = job.containerGroup?.containerType;
      return type?.id ? { id: String(type.id), name: type.name || `#${type.id}` } : none;
    }
    case "priority": {
      const priority = job.pickupLocation?.priority;
      const name = priority
        ? getEnumName(ConnectProto.PickupLocationPrioritySchema, priority)
        : undefined;
      return name ? { id: name, name: t(Namespace.Enum, name as TranslationKey) } : none;
    }
    case "geozone": {
      const zone = job.pickupLocation?.geoGroup?.trim();
      return zone ? { id: zone, name: zone } : none;
    }
    case "route":
      return job.routeId ? { id: String(job.routeId), name: `M${job.routeId}` } : none;
    case "plate": {
      const truck = job.driverOnShift?.truck;
      return truck?.id && truck.plateNumber
        ? { id: String(truck.id), name: truck.plateNumber }
        : none;
    }
    case "comment":
      return job.driverComment?.trim()
        ? { id: "yes", name: t(Namespace.Table, "yes") }
        : { id: "no", name: t(Namespace.Table, "no") };
  }
};

/** Whether a job passes every column filter set. A value not known yet does not pass. */
export const matchesColumnFilters = (
  job: ConnectProto.Job,
  values: ColumnFilterValues,
  t: TFn,
  incidentsOf: IncidentsOf,
): boolean =>
  COLUMN_FILTERS.every(
    (key) => values[key] === "" || valueOf(key, job, t, incidentsOf)?.id === values[key],
  );

/** A column filter's options: the values the given jobs have, each once, by name. */
export const columnFilterOptions = (
  key: ColumnFilter,
  jobs: readonly ConnectProto.Job[],
  t: TFn,
  incidentsOf: IncidentsOf,
): Option[] => {
  const options = new Map<string, Option>();
  for (const job of jobs) {
    const option = valueOf(key, job, t, incidentsOf);
    if (option) options.set(option.id, option);
  }
  return [...options.values()].sort((a, b) =>
    a.id === NONE ? 1 : b.id === NONE ? -1 : a.name.localeCompare(b.name),
  );
};
